import { useEffect, useState, type JSX } from 'react';
import {
  RadioButton as PrimeRadioButton,
  type RadioButtonChangeEvent,
  type RadioButtonProps as PrimeRadioButtonProps,
} from 'primereact/radiobutton';
import styles from './RadioButton.module.scss';

interface RadioOption {
  label: string;
  id: string;
}

/**
 * RadioButtonProps defines the properties for the RadioButton component.
 */
export interface RadioButtonProps {
  size?: 'Small' | 'Large';
  selected?: boolean;
  selectedValue?: string;
  disabled?: boolean;
  isLabel?: boolean;
  name: string;
  options: RadioOption[];
  onChange?: (value: string) => void;
  radioButtonProps?: Partial<PrimeRadioButtonProps>;
}

/**
 * Custom RadioButton component using PrimeReact's RadioButton for selecting a single option from a list, with support for custom sizing, label display, and selected/disabled states.
 *
 * ## Import path
 * ```tsx
 * import { RadioButton } from '@kpmg-global-audit-emu/react-ui-kit/atoms/RadioButton';
 * ```
 * @param {RadioButtonProps} props - Props including size, selected state, name, options, and change handler.
 * @returns {JSX.Element} The rendered radio button group.
 */
export const RadioButton = ({
  size = 'Small',
  options,
  selected,
  selectedValue,
  disabled,
  isLabel,
  name,
  radioButtonProps,
  onChange,
}: RadioButtonProps): JSX.Element => {
  const [internalValue, setInternalValue] = useState<string>('');

  const isControlled = selectedValue !== undefined;

  useEffect(() => {
    if (isControlled) {
      setInternalValue(selectedValue ?? '');
    }
  }, [selectedValue, isControlled]);

  const handleChange = (value: string) => {
    if (!selected) {
      if (onChange) {
        onChange(value);
      } else {
        setInternalValue(value);
      }
    }
  };

  const sizeClass = size?.toLowerCase();

  return (
    <div className={styles['radio-button-group']}>
      {options?.map((option) => {
        let isChecked = false;
        if (selected) {
          isChecked = true;
        } else if (isControlled) {
          isChecked = selectedValue === option.id;
        } else {
          isChecked = internalValue === option.id;
        }
        return (
          <div
            key={option.id}
            className={`${styles['radio-button-container']} ${styles[sizeClass]}`}
          >
            <PrimeRadioButton
              {...radioButtonProps}
              inputId={option.id}
              name={name}
              value={option.id}
              onChange={(e: RadioButtonChangeEvent) => handleChange(e.value)}
              checked={isChecked}
              disabled={disabled}
              className={[styles['radio-button'], radioButtonProps?.className]
                .filter(Boolean)
                .join(' ')}
              data-size={size === 'Small' ? 20 : 24}
            />
            {isLabel && <label htmlFor={option.id}>{option.label}</label>}
          </div>
        );
      })}
    </div>
  );
};
